// Lieferschein (Belegart 'delivery_note', LS-) und Rücknahme (Belegart 'sales_return', RT-): Kommissionieren,
// Warenausgang, Tour, Zustellung, Rücknahme – und die Liste, aus der das Modul Finanzen später berechnet.
//
//   Lieferschein   In Kommissionierung → Lieferbereit (gebucht) → Unterwegs → Zugestellt | Nicht zugestellt
//                  → Teilweise berechnet → Berechnet
//   Rücknahme      Entwurf → Gebucht → (Teilweise) gutgeschrieben
//
// Der Lieferschein bucht beim Festschreiben den Warenausgang am abgebenden Standort (feste Charge der Zeile, sonst
// FEFO) und belastet das Leergutkonto. Fahrzeug und Tourtag stehen neben dem Beleg und lassen sich bis zur
// Zustellung ändern. Mengen „im Gebinde“ in Tausendstel des Gebindes der Zeile.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { CONSIGNMENT_DELIVERY_TYPE, DELIVERY_OUTCOMES, DELIVERY_TYPE, ORDER_TYPE, RETURN_DISPOSITIONS, RETURN_TYPE } from '../lib/labels'
import { zCustomerRef, zDay, zDocumentRef, zEmptiesEntry, zLocationRef, zNote, zPerson, zReason, zUnitQty, zUnitQtyOrZero } from './common'
import { PROOF_BODY_BYTES, zDeliveryProofFile, zDeliveryProofInput } from './afterDelivery'

const DELIVERY_PERMISSIONS = { read: 'sales.delivery.read', write: 'sales.delivery.write', post: 'sales.delivery.write', reverse: 'sales.delivery.write' } as const
const RETURN_PERMISSIONS = { read: 'sales.delivery.read', write: 'sales.return.create', post: 'sales.return.create', reverse: 'sales.return.create' } as const

export const delivery = defineDocumentContracts({
  module: 'sales',
  entity: 'delivery',
  documentType: DELIVERY_TYPE,
  title: 'Lieferschein',
  titlePlural: 'Lieferscheine',
  permissions: DELIVERY_PERMISSIONS,
  derivesFrom: [ORDER_TYPE, CONSIGNMENT_DELIVERY_TYPE],
  keywords: ['Lieferschein', 'Lieferung', 'Warenausgang', 'ausliefern', 'LS'],
})

export const deliveryGet = delivery.get
export const deliveryList = delivery.list
export const deliverySaveDraft = delivery.saveDraft
/** „Lieferschein buchen“: Warenausgang, Pfand, Nummer – danach „Lieferbereit“ */
export const deliveryPost = delivery.post
export const deliveryReverse = delivery.reverse
export const deliveryPrint = delivery.print
export const deliveryDiscard = delivery.discard
/** Lieferschein aus einem Auftrag (Teillieferung: Teilmengen je Zeile) – der Rest bleibt am Auftrag offen */
export const deliveryDerive = delivery.derive

export const salesReturn = defineDocumentContracts({
  module: 'sales',
  entity: 'return',
  documentType: RETURN_TYPE,
  title: 'Rücknahme',
  titlePlural: 'Rücknahmen',
  permissions: RETURN_PERMISSIONS,
  derivesFrom: [DELIVERY_TYPE],
  keywords: ['Rücknahme', 'Retoure', 'Rücklieferung', 'Ware zurück', 'Annahme verweigert', 'RT'],
})

export const returnGet = salesReturn.get
export const returnList = salesReturn.list
export const returnSaveDraft = salesReturn.saveDraft
export const returnPost = salesReturn.post
export const returnReverse = salesReturn.reverse
export const returnPrint = salesReturn.print
export const returnDiscard = salesReturn.discard
export const returnDerive = salesReturn.derive

// ---- Kommissionieren ---------------------------------------------------------------------------------------------

const zPickProposal = z.object({
  lotId: zId.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  /** Kürzel des Lagerplatzes – '' ohne die Funktion „Lagerplätze“ */
  binCode: z.string(),
  /** Lagerplatz (nur mit der Funktion „Lagerplätze“) */
  binId: zId.nullable().default(null),
  binName: z.string().nullable().default(null),
  /** Reihenfolge des Platzes im Laufweg */
  binSort: z.int().default(0),
  /** Menge im Gebinde der Zeile (Tausendstel) */
  pickQty: z.int(),
})

/** Barcodes des Artikels: Code → Gebinde – der Ablauf am Telefon erkennt Scans ohne Rückfrage. */
const zPickBarcode = z.object({ code: z.string(), unitId: zId, baseQty: z.int() })

export const zPickLine = z.object({
  orderLineId: zId,
  pos: z.int(),
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  unitId: zId,
  unit: z.string(),
  unitBaseQty: z.int(),
  /** offen = bestellt − geliefert, im Gebinde der Zeile (Tausendstel) */
  openQty: z.int(),
  reservedQty: z.int(),
  /** davon jetzt kommissionierbar (reserviert + frei), nie mehr als offen */
  pickableQty: z.int(),
  lotTracked: z.boolean(),
  /** FEFO-Vorschlag: aus welchen Chargen und Plätzen die Menge kommt – in der Reihenfolge des Laufwegs */
  proposal: z.array(zPickProposal),
  minAge: z.int(),
  note: z.string().nullable(),
  barcodes: z.array(zPickBarcode).default([]),
})

export const zPickOrder = z.object({
  orderId: zId,
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  customer: zCustomerRef.nullable(),
  location: zLocationRef,
  deliveryDate: z.string().nullable(),
  timeWindow: z.string().nullable(),
  vehicle: zLocationRef.nullable(),
  channelLabel: z.string().nullable(),
  pickingNote: z.string().nullable(),
  /** „Lieferanschrift“ in einer Zeile */
  address: z.string().nullable(),
  /** Stopp des Auftrags auf einer offenen Tour (Disposition); null = nicht verplant */
  tourStop: z.object({ tourId: zId, tourName: z.string(), seq: z.int(), plannedEta: z.date().nullable() }).nullable().default(null),
  lines: z.array(zPickLine),
})

export const pickingList = defineContract({
  name: 'sales.picking.list',
  title: 'Kommissionierliste anzeigen',
  description: 'Offene Positionen bestätigter Aufträge je Auftrag oder Tourtag – mit FEFO-Vorschlag je Position.',
  llm: {
    description:
      'Liefert die Kommissionierliste: je bestätigtem, noch nicht vollständig geliefertem Auftrag die offenen ' +
      'Positionen mit Menge im Gebinde (Tausendstel), reservierter und jetzt kommissionierbarer Menge und dem ' +
      'Vorschlag nach FEFO (Lagerplatz, Warenposten, Ablaufdatum, Menge) – mit der Funktion „Lagerplätze“ in der Reihenfolge des ' +
      'Laufwegs. Filter: ein Auftrag (`orderId`), ein Tourtag (`deliveryDate`), ' +
      'Standort. Ändert nichts – gebucht wird mit sales.delivery.pick.',
    keywords: ['Kommissionierliste', 'Pickliste', 'kommissionieren', 'Tour vorbereiten', 'was ist zu packen'],
  },
  input: z.strictObject({
    orderId: zId.optional(),
    locationId: zId.optional(),
    /** Tourtag: Aufträge mit diesem Liefertermin (ohne Angabe alle offenen) */
    deliveryDate: zDay.optional(),
    /** Disposition (§18.4): die Aufträge dieser Tour in der Reihenfolge der Stopps */
    tourId: zId.optional(),
    /** mit `tourId`: Ladeliste – umgekehrte Stoppfolge, der zuletzt Auszuliefernde wird zuerst geladen */
    loadOrder: z.boolean().default(false),
    limit: z.int().min(1).max(100).default(50),
  }),
  output: z.object({
    orders: z.array(zPickOrder),
    today: z.string(),
    /** Funktion „Lagerplätze“ an: Plätze zeigen, Positionen nach dem Laufweg sortieren (sonst nach Position) */
    bins: z.boolean().default(false),
    /** die Tour, nach der die Liste geordnet ist (nur mit `tourId`) */
    tour: z.object({ id: zId, name: z.string(), tourDate: z.string(), vehicle: zLocationRef, loadOrder: z.boolean() }).nullable().default(null),
  }),
  permission: DELIVERY_PERMISSIONS.write,
  risk: 'read',
  scope: 'any',
})

export const PICK_OUTCOMES = ['posted', 'draft'] as const

export const deliveryPick = defineContract({
  name: 'sales.delivery.pick',
  title: 'Auftrag kommissionieren und Lieferschein buchen',
  description: 'Hält die kommissionierten Mengen eines Auftrags fest und bucht daraus den Lieferschein – Fehlmengen bleiben am Auftrag offen.',
  llm: {
    description:
      'Kommissioniert einen bestätigten Auftrag: je Auftragszeile die gepackte Menge im Gebinde der Zeile ' +
      '(Tausendstel; 0 = nichts gepackt; mehrere Einträge je Zeile möglich). `lotNo` nennt bei Betrieben mit Rückverfolgung den gepackten ' +
      'Warenposten (nie einen gesperrten); packt das Lager einen Warenposten, der später abläuft als der vorgeschlagene, ist `lotReason` ' +
      'Pflicht. `binCode` nennt mit der Funktion „Lagerplätze“ den Platz, von dem genommen wurde (nie ein gesperrter). Ohne ' +
      'Angabe teilt das System nach FEFO zu. Weniger als offen ist eine Fehlmenge: Sie wird am Lieferschein vermerkt und bleibt am ' +
      'Auftrag offen. Mehr als offen lässt sich nicht buchen. Mit `post: true` (Standard) entsteht der gebuchte ' +
      'Lieferschein: Warenausgang am Standort des Auftrags, Zustand „Lieferbereit“. ' +
      'Ohne `lines` wird alles Offene nach FEFO geliefert. Für die Zustellung danach: sales.delivery.confirm.',
    keywords: ['kommissionieren', 'Lieferschein buchen', 'packen', 'Warenausgang', 'Fehlmenge', 'Teillieferung'],
  },
  input: z.strictObject({
    orderId: zId,
    lines: z
      .array(
        z.strictObject({
          orderLineId: zId,
          /** gepackte Menge im Gebinde der Auftragszeile (Tausendstel) */
          pickedQty: zUnitQtyOrZero,
          lotNo: z.string().trim().max(80).nullish(),
          /** Kürzel des Lagerplatzes, von dem genommen wurde (Funktion „Lagerplätze“) */
          binCode: z.string().trim().min(1).max(40).nullish(),
          /** Grund, wenn eine andere Charge als vorgeschlagen (mit späterem MHD) gepackt wurde */
          lotReason: zNote,
          /** Grund einer Fehlmenge */
          shortReason: zNote,
        }),
      )
      .max(500)
      .optional(),
    /** Fahrzeug der Tour (Standort der Art Fahrzeug); ohne Angabe das am Auftrag geplante */
    vehicleLocationId: zId.nullish(),
    /** Tourtag; ohne Angabe der Liefertermin des Auftrags, sonst heute */
    tourDate: zDay.optional(),
    post: z.boolean().default(true),
  }),
  output: z.object({
    deliveryId: zId,
    number: z.string().nullable(),
    outcome: z.enum(PICK_OUTCOMES),
    /** Zeilen mit Fehlmenge */
    shortLineCount: z.int(),
    message: z.string(),
  }),
  permission: DELIVERY_PERMISSIONS.write,
  risk: 'financial',
  scope: 'record',
  limits: { maxItems: 500, itemCount: (input) => input.lines?.length ?? 1 },
})

// ---- Tour und Zustellung -----------------------------------------------------------------------------------------

export const deliveryAssign = defineContract({
  name: 'sales.delivery.assign',
  title: 'Lieferungen einem Fahrzeug zuordnen',
  description: 'Legt Fahrzeug und Tourtag gebuchter, noch nicht zugestellter Lieferscheine fest.',
  llm: {
    description:
      'Ordnet gebuchte, noch nicht zugestellte Lieferscheine einem Fahrzeug (Standort der Art Fahrzeug) und einem ' +
      'Tourtag zu oder nimmt die Zuordnung zurück (`vehicleLocationId: null` = Abholung). Der Fahrer sieht nur ' +
      'Lieferungen seines Fahrzeugs.',
    keywords: ['Tour planen', 'Fahrzeug zuordnen', 'verladen', 'Tour ändern'],
  },
  input: z.strictObject({
    ids: z.array(zId).min(1, { error: 'Bitte wählen Sie mindestens einen Lieferschein.' }).max(100),
    vehicleLocationId: zId.nullable(),
    tourDate: zDay.optional(),
  }),
  output: z.object({ updated: z.int(), message: z.string() }),
  permission: DELIVERY_PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
  limits: { maxItems: 100, itemCount: (input) => input.ids.length },
})

export const deliveryDispatch = defineContract({
  name: 'sales.delivery.dispatch',
  title: 'Tour starten',
  description: 'Setzt lieferbereite Lieferscheine auf „Unterwegs“.',
  llm: {
    description: 'Meldet lieferbereite Lieferscheine als unterwegs (das Fahrzeug hat den Hof verlassen). Bucht nichts – der Warenausgang ist mit dem Lieferschein gebucht.',
    keywords: ['Tour starten', 'unterwegs', 'losfahren', 'verladen'],
  },
  input: z.strictObject({ ids: z.array(zId).min(1, { error: 'Bitte wählen Sie mindestens einen Lieferschein.' }).max(100) }),
  output: z.object({ updated: z.int(), message: z.string() }),
  permission: DELIVERY_PERMISSIONS.write,
  risk: 'write',
  scope: 'record',
  limits: { maxItems: 100, itemCount: (input) => input.ids.length },
})

/** Zahlarten an der Tür: bar oder Karte (Beleg des Kartengeräts). */
export const DOOR_PAYMENT_METHODS = ['cash', 'card'] as const
export type DoorPaymentMethod = (typeof DOOR_PAYMENT_METHODS)[number]
export const DOOR_PAYMENT_LABELS: Record<DoorPaymentMethod, string> = { cash: 'bar', card: 'mit Karte' }
const zDoorCents = z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Der Betrag darf nicht negativ sein.' }).max(10_000_000, { error: 'Der Betrag ist zu groß.' })

export const deliveryConfirm = defineContract({
  name: 'sales.delivery.confirm',
  title: 'Zustellung bestätigen',
  description: 'Hält die Übergabe beim Kunden fest: geliefert wie notiert oder mit Abweichungen, Empfänger, Kassieren an der Tür.',
  llm: {
    description:
      'Bestätigt die Zustellung eines gebuchten Lieferscheins. `outcome`: delivered = wie notiert · differences = ' +
      'je Zeile in `lines` die tatsächlich übergebene Menge im Gebinde (Tausendstel); was fehlt, geht als Rücknahme ' +
      'zurück in den Bestand · not_delivered = nichts übergeben: Der Lieferschein ' +
      'wird storniert, die Ware ist zurück, der Auftrag ist wieder offen und reserviert (neu einplanen). ' +
      '`recipientName` nennt, wer angenommen hat. Verlangt ein Artikel der Lieferung ein Mindestalter, ist `ageChecked: true` Pflicht – ' +
      'protokolliert wird nur ja/nein, wer und wann. `empties` zählt, was der Fahrer an Rückgut mitnimmt (nur Betriebe, die es führen; Stück in ' +
      'Tausendstel): Es entsteht eine Gutschrift auf dem Kundenkonto. Kassiert der Fahrer an der ' +
      'Tür, nennt `collectedCents` den Betrag in Cent und `collectedMethod` die Zahlart (cash = bar, card = Karte) – ' +
      'höchstens der Wert der übergebenen Ware abzüglich dieser Gutschrift; ist sie höher ' +
      'als die Lieferung, nennt `emptiesPayoutCents`, was bar ausgezahlt wurde. Gebucht wird das Geld erst mit der ' +
      'Fahrerabrechnung (finance.driverCash.submit und …approve). Danach ist der Lieferschein „Zugestellt“ und ' +
      'abrechenbar. `proof` hält Unterschrift und Foto der Übergabe fest (Base64, JPEG oder PNG).',
    keywords: ['Zustellung bestätigen', 'zugestellt', 'abgeliefert', 'nicht angetroffen', 'Annahme verweigert', 'Alterskontrolle'],
  },
  input: z.strictObject({
    id: zId,
    outcome: z.enum(DELIVERY_OUTCOMES),
    recipientName: z.string().trim().max(120).nullish(),
    /** Alter bei der Übergabe kontrolliert (Pflicht bei Artikeln mit Mindestalter) */
    ageChecked: z.boolean().optional(),
    lines: z.array(z.strictObject({ lineId: zId, deliveredQty: zUnitQtyOrZero })).max(500).optional(),
    empties: z.array(zEmptiesEntry).max(50).optional(),
    /** an der Tür kassiert, in Cent – zusammen mit `collectedMethod` */
    collectedCents: zDoorCents.optional(),
    collectedMethod: z.enum(DOOR_PAYMENT_METHODS, { error: 'Bitte wählen Sie bar oder Karte.' }).optional(),
    /** für Leergut bar ausgezahlt, in Cent – nur wenn das Leergut mehr wert ist als die Lieferung */
    emptiesPayoutCents: zDoorCents.optional(),
    note: zNote,
    /** Zustellnachweis: Unterschrift (≤ 300 KB) und/oder Foto (≤ 2 MB) als JPEG oder PNG in Base64 */
    proof: zDeliveryProofInput.optional(),
  }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    returnId: zId.nullable(),
    returnNumber: z.string().nullable(),
    emptiesDocumentId: zId.nullable(),
    /** Pfandgutschrift für das mitgenommene Leergut in Cent (brutto) */
    emptiesCreditCents: z.int(),
    /** an der Tür fällig gewesen: übergebene Ware mit Pfand abzüglich Leergutgutschrift (negativ = Auszahlung) */
    dueCents: z.int(),
    /** an der Tür kassiert (bar oder Karte) bzw. für Leergut bar ausgezahlt */
    collectedCents: z.int(),
    emptiesPayoutCents: z.int(),
    message: z.string(),
  }),
  permission: 'sales.delivery.confirm',
  risk: 'financial',
  scope: 'record',
  limits: { maxItems: 500, itemCount: (input) => (input.lines?.length ?? 0) + (input.empties?.length ?? 0) + 1, bodyBytes: PROOF_BODY_BYTES },
})

export const zDeliveryProof = z.object({
  outcome: z.enum(DELIVERY_OUTCOMES),
  outcomeLabel: z.string(),
  recipientName: z.string().nullable(),
  ageCheckRequired: z.boolean(),
  ageChecked: z.boolean().nullable(),
  confirmedAt: z.date(),
  confirmedBy: zPerson.nullable(),
  note: z.string().nullable(),
  /** mitgenommenes Leergut: „3 Kasten 20 × 0,5 l“ */
  empties: z.array(z.object({ depositTypeId: zId, name: z.string(), emptiesQty: z.int() })),
  emptiesDocumentId: zId.nullable(),
  returnDocumentId: zId.nullable(),
  /** an der Tür kassiert bzw. für Leergut ausgezahlt – null, wenn kein Geld floss (Rechnung) */
  cash: z
    .object({
      dueCents: z.int(),
      cashCents: z.int(),
      cardCents: z.int(),
      payoutCents: z.int(),
      /** „82,06 € mit Karte kassiert“ */
      text: z.string(),
    })
    .nullable(),
  /** Unterschrift bei der Übergabe (Zustellnachweis) – null ohne */
  signature: zDeliveryProofFile.nullable().default(null),
  /** Foto bei der Übergabe – null ohne */
  photo: zDeliveryProofFile.nullable().default(null),
})

export const zDeliveryStop = z.object({
  id: zId,
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  customer: zCustomerRef.nullable(),
  /** Lieferanschrift in einer Zeile */
  address: z.string().nullable(),
  location: zLocationRef,
  vehicle: zLocationRef.nullable(),
  tourDate: z.string(),
  timeWindow: z.string().nullable(),
  driverNote: z.string().nullable(),
  orderNumber: z.string().nullable(),
  channelLabel: z.string().nullable(),
  /** „12 Ka Augustiner Hell · 2 Ka Spezi · +3“ */
  summary: z.string(),
  lineCount: z.int(),
  grossCents: z.int(),
  depositCents: z.int(),
  minAge: z.int(),
  ageNotice: z.string().nullable(),
  recipientName: z.string().nullable(),
  confirmedAt: z.date().nullable(),
  /** „82,06 € mit Karte kassiert“ – null, wenn an der Tür kein Geld floss */
  cashText: z.string().nullable(),
})

export const deliveryBoard = defineContract({
  name: 'sales.delivery.board',
  title: 'Lieferungen des Tages anzeigen',
  description: 'Lieferscheine eines Tourtags je Fahrzeug: lieferbereit, unterwegs, zugestellt.',
  llm: {
    description:
      'Listet die gebuchten Lieferscheine eines Tourtags (Standard: heute) mit Fahrzeug, Kunde, Anschrift, ' +
      'Zeitfenster und Zustand (Lieferbereit, Unterwegs, Zugestellt, Nicht zugestellt). Sichtbar ist, was am ' +
      'abgebenden Standort oder am Fahrzeug gelesen werden darf – ein Fahrer sieht nur sein Fahrzeug. `open: true` ' +
      'zeigt zusätzlich alles noch nicht Zugestellte früherer Tage.',
    keywords: ['Tour', 'Lieferungen heute', 'Fahrzeug', 'unterwegs', 'zugestellt', 'Ladeliste', 'meine Tour'],
  },
  input: z.strictObject({
    date: zDay.optional(),
    vehicleLocationId: zId.optional(),
    locationId: zId.optional(),
    open: z.boolean().default(true),
  }),
  output: z.object({
    date: z.string(),
    stops: z.array(zDeliveryStop),
    counts: z.object({ ready: z.int(), inTransit: z.int(), delivered: z.int(), notDelivered: z.int() }),
    /** an der Tür kassiert bei den gezeigten Lieferungen: bar (abzüglich Auszahlungen für Leergut) und Karte, in Cent */
    collected: z.object({ cashCents: z.int(), cardCents: z.int(), stopCount: z.int() }),
    /** Fahrzeuge, die der Aufrufer sehen darf (für Filter und Zuordnung) */
    vehicles: z.array(zLocationRef),
  }),
  permission: DELIVERY_PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})

export const deliveryDetail = defineContract({
  name: 'sales.delivery.detail',
  title: 'Stand einer Lieferung anzeigen',
  description: 'Fahrzeug, Tourtag, Positionen mit Fehlmengen und Zustellnachweis eines Lieferscheins.',
  llm: {
    description:
      'Liefert zu einem Lieferschein Tour und Zustellung: Fahrzeug, Tourtag, Zeitfenster, Hinweis für den Fahrer, je ' +
      'Position Menge im Gebinde (Tausendstel) und vermerkte Fehlmenge, Hinweise für die Übergabe, die ' +
      'Angaben für die Zählung an der Tür, den Zustellnachweis (Empfänger, Zeitpunkt) und die Rücknahmen ' +
      'dazu. Den Beleg selbst liefert sales.delivery.get.',
    keywords: ['Zustellnachweis', 'Empfänger', 'Tour', 'Fahrzeug', 'Fehlmenge'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    id: zId,
    number: z.string().nullable(),
    status: z.string(),
    statusLabel: z.string(),
    posted: z.boolean(),
    customer: zCustomerRef.nullable(),
    address: z.string().nullable(),
    /** Telefon für die Zustellung: aus der Bestellung (Lieferanschrift, Einmalkunde), sonst aus den Stammdaten des Kunden */
    phone: z.string().nullable(),
    location: zLocationRef,
    vehicle: zLocationRef.nullable(),
    tourDate: z.string().nullable(),
    timeWindow: z.string().nullable(),
    driverNote: z.string().nullable(),
    orderId: zId.nullable(),
    orderNumber: z.string().nullable(),
    grossCents: z.int(),
    depositCents: z.int(),
    lines: z.array(
      z.object({
        lineId: zId,
        pos: z.int(),
        itemId: zId.nullable(),
        sku: z.string().nullable(),
        description: z.string().nullable(),
        unit: z.string().nullable(),
        /** gelieferte Menge im Gebinde (Tausendstel) */
        deliveryQty: z.int(),
        returnedQty: z.int(),
        lotNo: z.string().nullable(),
        bestBefore: z.string().nullable(),
        /** beim Kommissionieren vermerkte Fehlmenge im Gebinde */
        shortQty: z.int(),
        shortReason: z.string().nullable(),
        minAge: z.int(),
      }),
    ),
    minAge: z.int(),
    ageNotice: z.string().nullable(),
    /** Pfandarten der gelieferten Gebinde und des Leergutkontos – Vorschlag für die Zählung beim Kunden */
    emptiesTypes: z.array(z.object({ depositTypeId: zId, name: z.string(), amountCents: z.int() })),
    proof: zDeliveryProof.nullable(),
    returns: z.array(zDocumentRef),
    canConfirm: z.boolean(),
    /** der Kunde hat ein Konto: An der Tür lässt sich kassieren bzw. Leergut auszahlen */
    canCollect: z.boolean(),
    canDispatch: z.boolean(),
    canAssign: z.boolean(),
    canReturn: z.boolean(),
  }),
  permission: DELIVERY_PERMISSIONS.read,
  risk: 'read',
  scope: 'record',
})

export const deliveryBillable = defineContract({
  name: 'sales.delivery.billable',
  title: 'Abrechenbare Lieferungen anzeigen',
  description: 'Zugestellte, noch nicht vollständig berechnete Lieferscheine – die Arbeitsliste für die Rechnungsstellung.',
  llm: {
    description:
      'Listet zugestellte Lieferscheine, die noch nicht (vollständig) berechnet sind, mit Kunde, Zustelltag, ' +
      'Lieferwert, bereits berechnetem Anteil, noch nicht gutgeschriebenen Rücknahmen und dem abrechenbaren Betrag ' +
      '(netto und brutto). Daraus leitet das Modul Finanzen Rechnungen ab (Belegart delivery_note → ' +
      'Rechnung, Rücknahme sales_return → Rechnungskorrektur). Filter: Kunde, Standort, zugestellt bis.',
    keywords: ['abrechenbar', 'noch nicht berechnet', 'Rechnung schreiben', 'offene Lieferscheine', 'Sammelrechnung'],
  },
  input: z.strictObject({ partnerId: zId.optional(), locationId: zId.optional(), until: zDay.optional(), limit: z.int().min(1).max(500).default(200) }),
  output: z.object({
    rows: z.array(
      z.object({
        id: zId,
        number: z.string().nullable(),
        status: z.string(),
        statusLabel: z.string(),
        customer: zCustomerRef.nullable(),
        locationCode: z.string(),
        documentDate: z.string(),
        deliveredAt: z.date().nullable(),
        orderNumber: z.string().nullable(),
        netCents: z.int(),
        grossCents: z.int(),
        depositCents: z.int(),
        /** bereits berechneter Anteil (brutto) */
        invoicedCents: z.int(),
        /** gebuchte, noch nicht gutgeschriebene Rücknahmen (brutto) */
        returnedCents: z.int(),
        billableNetCents: z.int(),
        billableGrossCents: z.int(),
        returns: z.array(zDocumentRef),
      }),
    ),
    totals: z.object({ count: z.int(), billableNetCents: z.int(), billableGrossCents: z.int() }),
  }),
  permission: DELIVERY_PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})

export const deliveryTodayCount = defineContract({
  name: 'sales.delivery.todayCount',
  title: 'Lieferungen des Tages zählen',
  description: 'Zählt lieferbereite, fahrende und zugestellte Lieferscheine des heutigen Tourtags – für Navigation und Startseite.',
  llm: {
    description: 'Zählt die Lieferscheine des heutigen Tourtags nach Zustand an den Standorten und Fahrzeugen des Nutzers. Für Details sales.delivery.board.',
    keywords: ['wie viele Lieferungen', 'Tour heute'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int(), ready: z.int(), inTransit: z.int(), delivered: z.int(), notDelivered: z.int(), toPick: z.int() }),
  permission: DELIVERY_PERMISSIONS.read,
  risk: 'read',
  scope: 'any',
})

// ---- Rücknahme ---------------------------------------------------------------------------------------------------

export const returnBook = defineContract({
  name: 'sales.return.book',
  title: 'Rücknahme zum Lieferschein buchen',
  description: 'Nimmt Ware eines Lieferscheins vom Kunden zurück – in den Bestand oder in den Sperrbestand.',
  llm: {
    description:
      'Bucht in einem Schritt die Rücknahme von Ware eines gebuchten Lieferscheins: je Lieferschein-Zeile die ' +
      'zurückgenommene Menge im Gebinde (Tausendstel) und wohin sie geht (`disposition`: stock = zurück in den ' +
      'verkaufsfähigen Bestand, blocked = Sperrbestand, etwa bei Bruch oder zweifelhafter Ware). Der Bestand steigt ' +
      'am abgebenden Standort zum damaligen Einstandswert; was mit der Ware berechnet war, wird ' +
      'zurückgeschrieben. Hat der Lieferschein eine Position aus mehreren Warenposten geliefert (nur Betriebe mit ' +
      'Rückverfolgung), verteilt das System die Rücknahme auf diese (zuletzt angebrochener zuerst); `lotNo` nennt ' +
      'den Warenposten ausdrücklich. ' +
      'Braucht einen Grund. Mehr als geliefert lässt sich nicht zurücknehmen. Die Rücknahme ist ' +
      'die Grundlage einer späteren Rechnungskorrektur.',
    keywords: ['Rücknahme', 'Retoure', 'Ware zurück', 'Annahme verweigert', 'falsch geliefert', 'Bruch beim Kunden'],
  },
  input: z.strictObject({
    deliveryId: zId,
    reason: zReason,
    documentDate: zDay.optional(),
    lines: z
      .array(
        z.strictObject({
          lineId: zId,
          returnQty: zUnitQty,
          disposition: z.enum(RETURN_DISPOSITIONS).default('stock'),
          /** Warenposten (Charge), in den zurückgenommen wird – ohne Angabe der gelieferte (bei mehreren: zuletzt angebrochener zuerst) */
          lotNo: z.string().trim().min(1).max(80).nullish(),
        }),
      )
      .min(1, { error: 'Bitte nennen Sie mindestens eine Position.' })
      .max(500),
  }),
  output: z.object({ returnId: zId, number: z.string().nullable(), document: zDocumentView, message: z.string() }),
  permission: RETURN_PERMISSIONS.post,
  risk: 'financial',
  scope: 'record',
  fieldPermissions: { 'document.lines.*.costCents': PERMISSION_COSTS_READ },
  limits: { maxItems: 500, itemCount: (input) => input.lines.length },
})
